/*eslint no-magic-numbers: ["error", { "ignore": [0] }]*/
import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'

import { requestGetSite, requestValidateSite, requestUpdateSite, requestSiteVisibility, requestValidateSiteGeometry, requestValidateEditOnlySite, requestGetEditOnlySite } from 'api/site'
import { useParams } from 'react-router-dom'
import { defaultSiteProposalValidationResults } from '../constants/defaultSiteProposalValidationResults'
import { showErrorToast } from 'utils/toast'
import { isEmpty } from 'lodash'

// Ensure all validation sections have defaults, especially when criterionE is missing
export const ensureValidationDefaults = (validation) => {
  const defaults = defaultSiteProposalValidationResults
  if (!validation.assessmentsAvailable && isEmpty(validation.criterionE)) {
    return {
      ...validation,
      assessmentParameters: validation.assessmentParameters || defaults.assessmentParameters,
      ecoSystem: validation.ecoSystem || defaults.ecoSystem,
      criterionC: validation.criterionC || defaults.criterionC,
      criterionE: {
        irreplaceability_assessment_approved: false,
        year: false,
        irreplaceability_score: false,
        trigger_reason_id: false,
        trigger_reason_text: false
      }
    }
  }
  return {
    ...validation,
    assessmentParameters: validation.assessmentParameters || defaults.assessmentParameters,
    ecoSystem: validation.ecoSystem || defaults.ecoSystem,
    criterionC: validation.criterionC || defaults.criterionC,
    criterionE: validation.criterionE || defaults.criterionE
  }
}

export const SiteContext = React.createContext({})

export const SiteProvider = ({ children }) => {
  const params = useParams()

  const [siteProposalFieldValidation, setSiteProposalFieldValidation] = useState(defaultSiteProposalValidationResults)
  const [triggerReasonId, setTriggerReasonId] = useState([])
  const [isEditable, setIsEditable] = useState(false)
  const [site, setSite] = useState({
    CountryCode: null,
    ProposerId: '',
    SiteStatusId: null,
    createdAt: '',
    'EcoRegion.id': null,
    'EcoRegion.name': '',
    id: '',
    name: '',
    updatedAt: '',
    year: null,
    biologicalProcesses: [],
    conservationActionsNeeded: [],
    ecologicalIntegrity: false,
    geographicallyRestrictedBiodiversity: [],
    irreplaceabilityThroughQuantitiveAnalysis: false,
    ongoingConservationActions: [],
    reviewersContactsThatCanComment: null,
    threatenedBiodiversity: [],
    siteNameNational: '',
    state: '',
    siteArea: '',
    siteAreaCalculated: '',
    latitudeOfMidPoint: '',
    longitudeOfMidPoint: '',
    lowestAltitude: '',
    highestAltitude: '',
    rationaleForSiteInformation: '',
    siteDescription: '',
    howIsTheSiteManaged: '',
    deliniationRationale: '',
    coveredByWhatProtectedAreas: '',
    additionalBiodiversityValues: '',
    indigenousGroups: '',
    landUseRegimesAtSite: '',
    overlapOECM: '',
    notes: '',
    siteFiles: [],
    calculatedProtectedArea: '',
    CoverageOfProtectedAreasId: null,
    AltitudeOfBathymetryId: null,
    EcoSystemsId: null,
    EcoSystems2LargestId: null,
    EcoSystems3LargestId: null,
    EcoSystemsSmallestId: null,
  })

  const getMySite = async () => {
    const editOnlySite = await requestGetEditOnlySite(params.id)
    if (editOnlySite.error) {
      const site = await requestGetSite(params.id, params.siteHistoryId)
      return setSite({ ...site, dateOfAssesment: (site.dateOfAssesment), ecologicalIntegrity: site.ecologicalIntegrity })
    }
    return setSite({ ...editOnlySite, dateOfAssesment: (editOnlySite.dateOfAssesment), ecologicalIntegrity: editOnlySite.ecologicalIntegrity })
  }

  const getMyEditOnlyApprovedSite = async () => {
    const site = await requestGetSite(null, params.siteHistoryId)
    return setSite({ ...site, dateOfAssesment: (site.dateOfAssesment), ecologicalIntegrity: site.ecologicalIntegrity })
  }

  const getIsSiteEditable = () => {
    requestSiteVisibility(params.id).then((data) => {
      if (data.error) {
        return showErrorToast(data.error, 'showBorder')
      }
      setIsEditable(data.permissions[0].edit)
    })
  }

  const getSiteValidation = async () => {
    const [validateSiteRes, validateGeometryRes] = await Promise.all([
      requestValidateSite(params.id, params.siteHistoryId),
      requestValidateSiteGeometry(params.id, params.siteHistoryId)
    ]);

    let validation = { ...validateSiteRes };

    if (!validateGeometryRes.error) {
      validation = { ...validation, ...validateGeometryRes };
    }

    validation = ensureValidationDefaults(validation);

    setSiteProposalFieldValidation({ ...validation, updated: true });
  };

  const getEditOnlySiteValidation = async () => {
    const validateSiteRes = await requestValidateEditOnlySite(params.id)
    const validation = { ...validateSiteRes, assessmentParameters: defaultSiteProposalValidationResults.assessmentParameters }
    validation.assessmentsAvailable = true
    validation.about.proposalPurpose = true
    return setSiteProposalFieldValidation({ ...validation, updated: true })
  }

  
  const updateSite = (requestBody) => {
    requestUpdateSite(site.id, params.siteHistoryId, requestBody).then((response) => {
      setSite((s) => {
        return { ...s, ...response, dateOfAssesment: (response.dateOfAssesment), assessmentDate: (response.assessmentDate) }
      })
    })
  }

  const updateTriggerReasonId = (newTriggerReasonId) => {
    setTriggerReasonId(newTriggerReasonId)
  }


  useEffect(() => {
    getMySite()
    getIsSiteEditable()
  }, [])

  const contextValue = React.useMemo(() => ({
    site,
    setSite,
    isEditable,
    updateSite,
    siteProposalFieldValidation,
    setSiteProposalFieldValidation,
    getSiteValidation,
    getMySite,
    triggerReasonId,
    updateTriggerReasonId,
    getEditOnlySiteValidation,
    getMyEditOnlyApprovedSite
  }), [
    site,
    setSite,
    isEditable,
    updateSite,
    siteProposalFieldValidation,
    setSiteProposalFieldValidation,
    getSiteValidation,
    getMySite,
    triggerReasonId,
    updateTriggerReasonId,
    getEditOnlySiteValidation,
    getMyEditOnlyApprovedSite
  ])

  if (!site.id && !site.SiteId) {
    return null
  }
  return (
    <SiteContext.Provider value={contextValue}>
      {children}
    </SiteContext.Provider>
  )
}

export default SiteProvider

SiteProvider.propTypes = {
  children: PropTypes.node.isRequired
}
